import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, FileText, Layers, DollarSign, RefreshCw, Mail, Phone, ClipboardList, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Logo } from "../components/Logo";
import { Badge } from "../components/ui/badge";

const STATUS_STYLES = {
  new: "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30",
  contacted: "bg-neon-yellow/15 text-neon-yellow border-neon-yellow/30",
  closed: "bg-white/10 text-white/50 border-white/20",
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-surface p-5">
      <div className="flex items-center justify-between">
        <Icon size={20} className={accent} />
      </div>
      <div className="mt-4 font-mono text-3xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wider text-white/45 mt-1">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, inq, proj, aud] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/inquiries"),
        api.get("/admin/project-requests"),
        api.get("/admin/audit-requests"),
      ]);
      setStats(s.data);
      setInquiries(inq.data);
      setProjects(proj.data);
      setAudits(aud.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (kind, id, status) => {
    const path = kind === "inquiry" ? "inquiries" : kind === "audit" ? "audit-requests" : "project-requests";
    await api.patch(`/admin/${path}/${id}`, { status });
    load();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="min-h-screen bg-void">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-surface/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo size="text-lg" />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/50">{user?.email}</span>
            <button data-testid="admin-refresh" onClick={load} className="text-white/60 hover:text-neon-cyan transition-colors">
              <RefreshCw size={18} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm border border-white/15 hover:border-neon-cyan rounded-full px-4 py-1.5 transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight mb-8">Dashboard</h1>

        {/* Stats */}
        <div data-testid="admin-stats" className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
          <StatCard icon={Layers} label="Project requests" value={stats?.total_projects ?? "—"} accent="text-neon-cyan" />
          <StatCard icon={Inbox} label="Inquiries" value={stats?.total_inquiries ?? "—"} accent="text-neon-yellow" />
          <StatCard icon={ClipboardList} label="Audit requests" value={stats?.total_audits ?? "—"} accent="text-neon-cyan" />
          <StatCard icon={FileText} label="New projects" value={stats?.new_projects ?? "—"} accent="text-neon-cyan" />
          <StatCard icon={Mail} label="New inquiries" value={stats?.new_inquiries ?? "—"} accent="text-neon-yellow" />
          <StatCard icon={DollarSign} label="Pipeline value" value={stats ? `$${(stats.pipeline_value || 0).toLocaleString()}` : "—"} accent="text-neon-magenta" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            data-testid="admin-tab-projects"
            onClick={() => setTab("projects")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${tab === "projects" ? "bg-neon-cyan text-black" : "border border-white/15 text-white/60 hover:border-white/30"}`}
          >
            Project Requests ({projects.length})
          </button>
          <button
            data-testid="admin-tab-inquiries"
            onClick={() => setTab("inquiries")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${tab === "inquiries" ? "bg-neon-cyan text-black" : "border border-white/15 text-white/60 hover:border-white/30"}`}
          >
            Inquiries ({inquiries.length})
          </button>
          <button
            data-testid="admin-tab-audits"
            onClick={() => setTab("audits")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${tab === "audits" ? "bg-neon-cyan text-black" : "border border-white/15 text-white/60 hover:border-white/30"}`}
          >
            Audit Requests ({audits.length})
          </button>
        </div>

        {loading ? (
          <div className="font-mono text-neon-cyan animate-glow-pulse py-20 text-center">loading…</div>
        ) : tab === "projects" ? (
          <ProjectsTable projects={projects} fmtDate={fmtDate} updateStatus={updateStatus} />
        ) : tab === "audits" ? (
          <AuditsTable audits={audits} fmtDate={fmtDate} updateStatus={updateStatus} />
        ) : (
          <InquiriesTable inquiries={inquiries} fmtDate={fmtDate} updateStatus={updateStatus} />
        )}
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={`${STATUS_STYLES[status] || STATUS_STYLES.new} font-mono text-[10px] uppercase`}>
      {status}
    </Badge>
  );
}

function StatusButtons({ kind, id, current, updateStatus }) {
  const next = ["new", "contacted", "closed"];
  return (
    <div className="flex gap-1.5">
      {next.map((s) => (
        <button
          key={s}
          data-testid={`status-${kind}-${id}-${s}`}
          onClick={() => updateStatus(kind, id, s)}
          className={`text-[10px] font-mono uppercase px-2 py-1 rounded border transition-colors ${current === s ? "border-neon-cyan text-neon-cyan" : "border-white/10 text-white/40 hover:text-white"}`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function ProjectsTable({ projects, fmtDate, updateStatus }) {
  if (!projects.length) return <Empty label="No project requests yet." />;
  return (
    <div className="grid gap-4" data-testid="admin-projects-list">
      {projects.map((p) => (
        <div key={p.id} className="rounded-xl border border-white/10 bg-surface p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-lg">{p.name}</h3>
                <StatusBadge status={p.status} />
                {p.speed === "rush" && <Badge className="bg-neon-yellow/15 text-neon-yellow border-neon-yellow/30 font-mono text-[10px]">RUSH</Badge>}
              </div>
              <div className="flex flex-wrap gap-4 mt-1 text-sm text-white/50">
                <a href={`mailto:${p.email}`} className="hover:text-neon-cyan flex items-center gap-1"><Mail size={13} />{p.email}</a>
                {p.phone && <span className="flex items-center gap-1"><Phone size={13} />{p.phone}</span>}
                {p.company && <span>{p.company}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-2xl font-bold text-neon-cyan">${(p.total_price || 0).toLocaleString()}</div>
              <div className="font-mono text-xs text-neon-yellow">~{p.total_days} days · {p.app_type}</div>
            </div>
          </div>
          {p.features?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {p.features.map((f, i) => (
                <span key={i} className="text-[11px] font-mono text-white/50 border border-white/10 rounded px-2 py-0.5">{f}</span>
              ))}
            </div>
          )}
          {p.message && <p className="mt-3 text-sm text-white/60 italic">"{p.message}"</p>}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
            <span className="font-mono text-xs text-white/35">{fmtDate(p.created_at)}</span>
            <StatusButtons kind="project" id={p.id} current={p.status} updateStatus={updateStatus} />
          </div>
        </div>
      ))}
    </div>
  );
}

function InquiriesTable({ inquiries, fmtDate, updateStatus }) {
  if (!inquiries.length) return <Empty label="No inquiries yet." />;
  return (
    <div className="grid gap-4" data-testid="admin-inquiries-list">
      {inquiries.map((q) => (
        <div key={q.id} className="rounded-xl border border-white/10 bg-surface p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-lg">{q.name}</h3>
                <StatusBadge status={q.status} />
              </div>
              <div className="flex flex-wrap gap-4 mt-1 text-sm text-white/50">
                <a href={`mailto:${q.email}`} className="hover:text-neon-cyan flex items-center gap-1"><Mail size={13} />{q.email}</a>
                {q.phone && <span className="flex items-center gap-1"><Phone size={13} />{q.phone}</span>}
                {q.company && <span>{q.company}</span>}
              </div>
            </div>
            <span className="font-mono text-xs text-white/35">{fmtDate(q.created_at)}</span>
          </div>
          <p className="mt-3 text-sm text-white/70">{q.message}</p>
          <div className="flex justify-end mt-4 pt-3 border-t border-white/10">
            <StatusButtons kind="inquiry" id={q.id} current={q.status} updateStatus={updateStatus} />
          </div>
        </div>
      ))}
    </div>
  );
}

function AiAnalysisPanel({ analysis, id }) {
  return (
    <div data-testid={`audit-analysis-${id}`} className="mt-3 rounded-lg border border-neon-yellow/20 bg-neon-yellow/[0.03] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={13} className="text-neon-yellow" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-neon-yellow">AI Opportunity Analysis</span>
      </div>
      <p className="text-sm text-white/75">{analysis.summary}</p>
      {analysis.opportunities?.length > 0 && (
        <div className="mt-3 grid gap-2">
          {analysis.opportunities.map((o, i) => (
            <div key={i} className="rounded border border-white/10 bg-void/40 px-3 py-2">
              <div className="text-xs font-semibold text-neon-cyan">{o.title}</div>
              <div className="text-xs text-white/55 mt-0.5">{o.description}</div>
            </div>
          ))}
        </div>
      )}
      {analysis.quick_win && (
        <div className="mt-3 text-xs">
          <span className="font-mono uppercase text-[10px] text-neon-yellow mr-2">Quick win:</span>
          <span className="text-white/70">{analysis.quick_win}</span>
        </div>
      )}
      {analysis.talking_points?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {analysis.talking_points.map((t, i) => (
            <li key={i} className="text-xs text-white/55 flex gap-2">
              <span className="text-neon-cyan font-mono">›</span> {t}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-white/15 py-20 text-center text-white/40 font-mono text-sm">
      {label}
    </div>
  );
}

function AuditsTable({ audits, fmtDate, updateStatus }) {
  if (!audits.length) return <Empty label="No audit requests yet." />;
  return (
    <div className="grid gap-4" data-testid="admin-audits-list">
      {audits.map((a) => (
        <div key={a.id} className="rounded-xl border border-white/10 bg-surface p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-lg">{a.name}</h3>
                <StatusBadge status={a.status} />
              </div>
              <div className="flex flex-wrap gap-4 mt-1 text-sm text-white/50">
                <a href={`mailto:${a.email}`} className="hover:text-neon-cyan flex items-center gap-1"><Mail size={13} />{a.email}</a>
                {a.phone && <span className="flex items-center gap-1"><Phone size={13} />{a.phone}</span>}
                {a.company && <span>{a.company}</span>}
              </div>
            </div>
            <span className="font-mono text-xs text-white/35">{fmtDate(a.created_at)}</span>
          </div>
          <div className="mt-3 rounded-lg border border-neon-cyan/15 bg-void/50 p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-neon-cyan mb-1">Manual workflow to automate</div>
            <p className="text-sm text-white/70">{a.workflow}</p>
          </div>
          {a.ai_analysis ? (
            <AiAnalysisPanel analysis={a.ai_analysis} id={a.id} />
          ) : (
            <div data-testid={`audit-analysis-pending-${a.id}`} className="mt-3 flex items-center gap-2 font-mono text-[11px] text-white/35">
              <Sparkles size={12} className="text-neon-yellow" /> AI opportunity analysis generating… refresh shortly.
            </div>
          )}
          <div className="flex justify-end mt-4 pt-3 border-t border-white/10">
            <StatusButtons kind="audit" id={a.id} current={a.status} updateStatus={updateStatus} />
          </div>
        </div>
      ))}
    </div>
  );
}
